import TimelineBreakdown from './TimelineBreakdown';
import TimelineChart from './TimelineChart';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPackTimeLine, setTestFilter, setPage } from '../../../../../apps/frontend/src/redux/slices/packSlice';
import { useNavigate } from 'react-router-dom';
import TestFilterTabs from '../generic/TestFilterTabs';
import PackPagination from '../generic/PackPagination';
import { ArrowLeft } from '@phosphor-icons/react';
import PackButton from '../generic/PackButton';

const TAB_FILTERS = [
  { id: 1, name: 'Last test', value: 'last' },
  { id: 2, name: 'Last 3 tests', value: 'last3' },
  { id: 3, name: 'Last 5 tests', value: 'last5' },
  { id: 4, name: 'Last 10 tests', value: 'last10' },
  { id: 5, name: 'All tests', value: 'all' },
];

const Timeline = ({ pack, onResetFilters }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Scroll states
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isDescriptionVisible, setIsDescriptionVisible] = useState(true);
  const scrollContainerRef = useRef(null);

  const packId = localStorage.getItem('selectedPackId');

  const {
    timeLine: { data, loading },
    filters,
  } = useSelector((state) => state.pack);

  // ✅ Fetch timeline whenever packId, page, or testFilter changes
  useEffect(() => {
    if (!packId) return;
    dispatch(fetchPackTimeLine({ packId, page: filters?.page || 1, testFilter: filters?.testFilter }));
  }, [dispatch, packId, filters?.page, filters?.testFilter]);

  // ✅ Initialize filter if not set
  useEffect(() => {
    if (!filters?.testFilter) {
      dispatch(setTestFilter(TAB_FILTERS[0].value));
    }
  }, [dispatch, filters]);

  // Hide-on-scroll logic
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const currentScrollY = scrollContainerRef.current.scrollTop;
      const isTabletOrBelow = window.innerWidth < 1024;

      if (!isTabletOrBelow) {
        if (currentScrollY <= 0) {
          if (!isDescriptionVisible) setIsDescriptionVisible(true);
        } else if (currentScrollY > 70 && isDescriptionVisible) {
          setIsDescriptionVisible(false);
        } else if (currentScrollY < 30 && !isDescriptionVisible) {
          setIsDescriptionVisible(true);
        }
        setIsHeaderVisible(true);
        return;
      }

      if (currentScrollY < lastScrollY) {
        setIsHeaderVisible(true);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsHeaderVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [lastScrollY, isDescriptionVisible]);

  const breadcrumbItems = ['Jee Main Test Series', 'Pack Analysis', 'Timeline'];

  const totalPages = data?.totalPagesAvailable || 1;
  const activePage = filters?.page || 1;

  return (
    <div className="w-full max-w-4xl mx-auto flex flex-col h-screen overflow-y-auto" ref={scrollContainerRef}>
      {/* Header */}
      <div
        className={`z-10 q3-glass rounded transition-transform duration-300 ease-in-out lg:translate-y-0 ${
          isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="p-6 pb-4">
          {/* Mobile Back Button */}
          <PackButton navigateTo="/v2/home" />

          <div className="mb-6">
            <h1 className="text-3xl font-bold text-[var(--q3-neutral-default)]">Timeline</h1>
          </div>

          {/* ✅ Use Redux filter directly */}
          <TestFilterTabs />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="p-6 pt-2 space-y-8">
        <TimelineBreakdown
          data={data?.timeline}
          loading={loading}
          subjects={pack?.subjects}
          packId={packId}
          pack={pack}
          onResetFilters={onResetFilters}
        />
        <TimelineChart data={data?.timeline} onResetFilters={onResetFilters} />

        {/* Pagination */}
        {totalPages > 1 && (
          <PackPagination
            totalPages={totalPages}
            activePage={activePage}
            onPageClick={(page) => dispatch(setPage(page))}
          />
        )}
      </div>
    </div>
  );
};

export default Timeline;
